import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { generateMemoToken } from 'src/modules/shared/utils/memo.util';

/**
 * Represents a navigable section with an identifier and display label.
 */
export interface SectionNavigationItem {
  id: string;
  label: string;
}

/**
 * Configuration options for the useSectionNavigation hook.
 */
interface UseSectionNavigationOptions {
  sections: SectionNavigationItem[];
  defaultSectionId?: string;
}

/**
 * Return value for the useSectionNavigation hook, providing section state and navigation handler.
 */
interface UseSectionNavigationReturn {
  activeSectionId?: string;
  handleSectionChange: (sectionId: string) => void;
}

/**
 * Hook for managing section-based navigation within a page or component.
 *
 * Handles:
 * - Active section state management
 * - URL hash synchronization for bookmarkable sections
 * - Smooth scrolling to section elements
 * - Validation of section IDs against available sections
 *
 * @param options - Configuration including sections list and default section
 * @returns Object with current active section ID and section change handler
 *
 * @example
 * ```ts
 * const { activeSectionId, handleSectionChange } = useSectionNavigation({
 *   sections: [
 *     { id: 'overview', label: 'Overview' },
 *     { id: 'details', label: 'Details' }
 *   ],
 *   defaultSectionId: 'overview'
 * });
 * ```
 */
export const useSectionNavigation = ({
  sections,
  defaultSectionId,
}: UseSectionNavigationOptions): UseSectionNavigationReturn => {
  const navigate = useNavigate();
  const routerState = useRouterState();
  const [activeSectionId, setActiveSectionId] = useState<string | undefined>(
    defaultSectionId || sections[0]?.id
  );
  const scrollBehaviorRef = useRef<ScrollBehavior>('auto');

  const sectionIds = useMemo(
    () => sections.map((section) => section.id),
    [sections]
  );

  useEffect(() => {
    if (!sectionIds.length) return;
    if (activeSectionId && sectionIds.includes(activeSectionId)) return;

    setActiveSectionId(
      sectionIds.includes(defaultSectionId || '')
        ? defaultSectionId
        : sectionIds[0]
    );
  }, [activeSectionId, defaultSectionId, sectionIds]);

  const locationHashToken = useMemo(
    () =>
      generateMemoToken(
        routerState.location as unknown as Record<string, unknown>,
        ['hash']
      ),
    [routerState.location]
  );
  const normalizedHash = useMemo(() => {
    try {
      const parsed = JSON.parse(locationHashToken) as { hash?: string };
      return (parsed.hash || '').replace(/^#/, '');
    } catch {
      return '';
    }
  }, [locationHashToken]);

  useEffect(() => {
    if (!normalizedHash) return;
    if (!sectionIds.includes(normalizedHash)) return;

    setActiveSectionId(normalizedHash);

    const element = document.getElementById(normalizedHash);
    if (element) {
      element.scrollIntoView({
        behavior: scrollBehaviorRef.current,
        block: 'start',
      });
    }

    scrollBehaviorRef.current = 'auto';
  }, [normalizedHash, sectionIds]);

  const handleSectionChange = (sectionId: string) => {
    if (!sectionIds.includes(sectionId)) return;

    setActiveSectionId(sectionId);
    scrollBehaviorRef.current = 'smooth';

    navigate({
      to: '.',
      hash: sectionId,
      replace: true,
      hashScrollIntoView: false,
      ignoreBlocker: true,
    });
  };

  return { activeSectionId, handleSectionChange };
};
